import { useMemo } from "react";

import { NotificationRow } from "@/components/doc/left-sidebar/top-menu/notifications/NotificationRow";
import { NotificationText } from "@/components/doc/left-sidebar/top-menu/notifications/NotificationText";
import { useStore } from "@/store";
import { useChats } from "@/store/useChats";
import { chatPageUrl, docPageUrl } from "@/urls";
import { extractMessageBodyText } from "@/utils/extractMessageBodyText";

import type { MessageNotification, Notification } from "@/api";

export function MessageNotificationItem({
  notification,
  entity,
}: {
  notification: Notification;
  entity: MessageNotification;
}) {
  const { document, body } = entity;

  const store = useStore();
  const chats = useChats();

  const preview = useMemo(() => extractMessageBodyText(body), [body]);

  const href = document
    ? docPageUrl({
        docId: document.uid,
        chat: entity.chatUid ?? undefined,
        message: entity.messageUid,
      })
    : chatPageUrl({ chatId: entity.chatUid, message: entity.messageUid });

  // Navigating to the already open doc would reload it
  const openInPlace = () => {
    if (!document || !entity.chatUid || store.doc?.id !== document.uid) return false;
    void chats.openChatByUid(entity.chatUid, entity.messageUid);
    return true;
  };

  return (
    <NotificationRow
      notification={notification}
      eventType="message"
      badge="comment"
      href={href}
      onNavigate={openInPlace}
    >
      <NotificationText
        notification={notification}
        docId={document?.uid}
        docTitle={document?.title}
        chatUid={entity.chatUid}
        chatTitle={entity.chatTitle}
        copyKey={document ? "message.body" : "message.bodyChat"}
      />
      {preview && (
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 line-clamp-2">{preview}</p>
      )}
    </NotificationRow>
  );
}
